import React, { useState, useEffect } from 'react';
import { FileText, Plus, Calendar, Clock, CheckCircle, XCircle, AlertCircle, Download, Edit, Trash2 } from 'lucide-react';
import api from '../services/api';
import { STANDARD_DOCUMENT_TYPES, fetchDocumentTypes, documentTypeOptions, documentTypeName } from '../utils/documentTypes';
import './AttestationRequests.css';

const AttestationRequests = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingRequestId, setEditingRequestId] = useState(null);
  const [formData, setFormData] = useState({
    attestationType: 'work_certificate',
    language: 'fr',
    purpose: '',
    additionalNotes: '',
    startDate: '',
    endDate: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  // The document types are managed by the admin; they are shown in the language of the document
  const [documentTypes, setDocumentTypes] = useState(STANDARD_DOCUMENT_TYPES);
  const attestationTypes = documentTypeOptions(documentTypes, formData.language || 'fr');

  const statusConfig = {
    pending: { label: 'Pending', color: '#f59e0b', icon: Clock },
    approved: { label: 'Approved', color: '#10b981', icon: CheckCircle },
    rejected: { label: 'Rejected', color: '#ef4444', icon: XCircle },
    generated: { label: 'Generated', color: '#3b82f6', icon: Download },
  };

  useEffect(() => {
    fetchRequests();
    fetchDocumentTypes().then(setDocumentTypes);
  }, []);

  const fetchRequests = async () => {
    try {
      const response = await api.get('/attestations/my-requests');
      setRequests(response || []);
    } catch (error) {
      console.error('Error fetching requests:', error);
      setMessage({ type: 'error', text: 'Error loading requests.' });
    } finally {
      setLoading(false);
    }
  };

  const handleOpenCreateModal = () => {
    setEditingRequestId(null);
    setFormData({
      attestationType: 'work_certificate',
      language: 'fr',
      purpose: '',
      additionalNotes: '',
      startDate: '',
      endDate: '',
    });
    setShowForm(true);
  };

  const handleEdit = (request) => {
    setEditingRequestId(request.id);
    setFormData({
      attestationType: request.attestationType || 'work_certificate',
      language: request.language || 'fr',
      purpose: request.purpose || '',
      additionalNotes: request.additionalNotes || '',
      startDate: request.startDate ? request.startDate.substring(0, 10) : '',
      endDate: request.endDate ? request.endDate.substring(0, 10) : '',
    });
    setShowForm(true);
  };

  const handleDelete = async (requestId) => {
    if (!window.confirm('Are you sure you want to delete this pending request?')) return;
    setSubmitting(true);
    try {
      await api.delete(`/attestations/${requestId}`);
      setMessage({ type: 'success', text: 'Request deleted successfully.' });
      fetchRequests();
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.message || 'Error deleting request.' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setMessage({ type: '', text: '' });

    try {
      if (
        formData.startDate
        && formData.endDate
        && new Date(formData.endDate) < new Date(formData.startDate)
      ) {
        setMessage({ type: 'error', text: 'End date cannot be before start date.' });
        setSubmitting(false);
        return;
      }

      // Empty optional fields (dates) are rejected by the API validation: leave them out
      const payload = Object.fromEntries(Object.entries(formData).filter(([, value]) => value !== ''));

      if (editingRequestId) {
        await api.patch(`/attestations/${editingRequestId}`, payload);
        setMessage({ type: 'success', text: 'Request updated successfully!' });
      } else {
        await api.post('/attestations', payload);
        setMessage({ type: 'success', text: 'Request submitted successfully!' });
      }

      setShowForm(false);
      setEditingRequestId(null);
      setFormData({
        attestationType: 'work_certificate',
        language: 'fr',
        purpose: '',
        additionalNotes: '',
        startDate: '',
        endDate: '',
      });
      fetchRequests();
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.message || 'Error saving request.' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDownload = async (requestId, fileName) => {
    try {
      await api.downloadAttestation(requestId, fileName);
    } catch (error) {
      console.error('Error downloading file:', error);
      setMessage({ type: 'error', text: `Error downloading certificate: ${error.message}` });
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US');
  };

  if (loading) {
    return (
      <div className="attestation-requests">
        <div className="page-loading">Loading requests...</div>
      </div>
    );
  }

  return (
    <div className="attestation-requests">
      <div className="page-header">
        <h1><FileText size={24} /> Attestation Requests</h1>
        <p>Manage your work certificate requests</p>
        <button className="btn btn-primary" onClick={handleOpenCreateModal}>
          <Plus size={16} /> New Request
        </button>
      </div>

      {message.text && (
        <div className={`message ${message.type}`}>
          {message.text}
        </div>
      )}

      {showForm && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h2>{editingRequestId ? 'Edit Attestation Request' : 'New Attestation Request'}</h2>
              <button className="close-btn" onClick={() => setShowForm(false)}>×</button>
            </div>
            
            <form onSubmit={handleSubmit} className="attestation-form">
              <div className="form-group">
                <label>Certificate Type *</label>
                <select
                  value={formData.attestationType}
                  onChange={(e) => setFormData({ ...formData, attestationType: e.target.value })}
                  required
                >
                  {attestationTypes.map(type => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Document Language *</label>
                <select
                  value={formData.language || 'fr'}
                  onChange={(e) => setFormData({ ...formData, language: e.target.value })}
                >
                  <option value="fr">Français</option>
                  <option value="en">English</option>
                </select>
              </div>

              <div className="form-group">
                <label>Purpose</label>
                <textarea
                  value={formData.purpose}
                  onChange={(e) => setFormData({ ...formData, purpose: e.target.value })}
                  placeholder="Why do you need this certificate?"
                  rows={3}
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Start Date</label>
                  <input
                    type="date"
                    value={formData.startDate}
                    onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>End Date</label>
                  <input
                    type="date"
                    value={formData.endDate}
                    min={formData.startDate || undefined}
                    onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Additional Notes</label>
                <textarea
                  value={formData.additionalNotes}
                  onChange={(e) => setFormData({ ...formData, additionalNotes: e.target.value })}
                  placeholder="Additional information..."
                  rows={3}
                />
              </div>

              <div className="form-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setShowForm(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={submitting}>
                  {submitting ? 'Saving...' : (editingRequestId ? 'Update Request' : 'Submit Request')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="requests-list">
        {requests.length === 0 ? (
          <div className="empty-state">
            <FileText size={48} />
            <h3>No Requests</h3>
            <p>You haven't made any attestation requests yet.</p>
            <button className="btn btn-primary" onClick={handleOpenCreateModal}>
              <Plus size={16} /> Create Request
            </button>
          </div>
        ) : (
          requests.map((request) => {
            const statusInfo = statusConfig[request.status];
            const StatusIcon = statusInfo.icon;
            const typeInfo = { label: documentTypeName(documentTypes, request.attestationType, request.language || 'fr') };
            
            return (
              <div key={request.id} className="request-card">
                <div className="request-header">
                  <div className="request-type">
                    <h3>{typeInfo?.label || request.attestationType} <small>({String(request.language || 'fr').toUpperCase()})</small></h3>
                    <span className="type-description">{typeInfo?.description}</span>
                  </div>
                  <div className="request-status" style={{ color: statusInfo.color }}>
                    <StatusIcon size={16} />
                    {statusInfo.label}
                  </div>
                </div>

                <div className="request-details">
                  {request.purpose && (
                    <div className="detail-item">
                      <strong>Purpose:</strong> {request.purpose}
                    </div>
                  )}
                  
                  {(request.startDate || request.endDate) && (
                    <div className="detail-item">
                      <Calendar size={14} />
                      {request.startDate && `From ${formatDate(request.startDate)}`}
                      {request.startDate && request.endDate && ' '}
                      {request.endDate && `to ${formatDate(request.endDate)}`}
                    </div>
                  )}

                  {request.additionalNotes && (
                    <div className="detail-item">
                      <strong>Notes:</strong> {request.additionalNotes}
                    </div>
                  )}

                  {request.rejectionReason && (
                    <div className="detail-item rejection">
                      <AlertCircle size={14} />
                      <strong>Rejection Reason:</strong> {request.rejectionReason}
                    </div>
                  )}
                </div>

                <div className="request-footer">
                  <div className="request-dates">
                    <span>Created on {formatDate(request.createdAt)}</span>
                    {request.updatedAt !== request.createdAt && (
                      <span>Updated on {formatDate(request.updatedAt)}</span>
                    )}
                  </div>
                  
                  <div className="request-actions">
                    {request.status === 'pending' && (
                      <>
                        <button
                          className="btn btn-sm btn-outline"
                          onClick={() => handleEdit(request)}
                          disabled={submitting}
                          title="Edit request"
                          style={{ marginRight: '6px' }}
                        >
                          <Edit size={14} /> Edit
                        </button>
                        <button
                          className="btn btn-sm btn-danger"
                          onClick={() => handleDelete(request.id)}
                          disabled={submitting}
                          title="Delete request"
                        >
                          <Trash2 size={14} /> Delete
                        </button>
                      </>
                    )}
                    
                    {request.status === 'generated' && request.generatedFilePath && (
                      <button 
                        className="btn btn-sm btn-outline"
                        onClick={() => handleDownload(request.id, `${request.attestationType}_${request.id}`)}
                      >
                        <Download size={14} />
                        Download PDF
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default AttestationRequests;

